import { useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Loader2, LogIn } from "lucide-react";
import { AuthCard } from "@/components/auth/AuthCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password, remember);
    } catch (err) {
      setError(apiError(err, "Accesso non riuscito"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthCard testId="login-card" title="Accedi" subtitle="Accesso riservato alle persone autorizzate">
      <form onSubmit={submit} data-testid="login-form" className="space-y-5">
        <div className="space-y-1.5">
          <Label htmlFor="login-email" className="text-xs font-medium text-slate-600">Email</Label>
          <Input id="login-email" data-testid="login-email-input" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 rounded-xl" />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="login-password" className="text-xs font-medium text-slate-600">Password</Label>
            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-medium text-emerald-700 underline-offset-2 hover:underline">Password dimenticata?</Link>
          </div>
          <Input id="login-password" data-testid="login-password-input" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 rounded-xl" />
        </div>
        <label htmlFor="login-remember" className="flex cursor-pointer items-center gap-2.5 text-sm text-slate-700">
          <Checkbox id="login-remember" data-testid="login-remember-checkbox" checked={remember} onCheckedChange={(v) => setRemember(v === true)} className="h-5 w-5 rounded-[5px] data-[state=checked]:border-emerald-600 data-[state=checked]:bg-emerald-600" />
          Resta collegato su questo dispositivo
        </label>
        {error && <p data-testid="login-error" className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}
        <Button type="submit" data-testid="login-submit-btn" disabled={busy} className="h-11 w-full gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />} Accedi
        </Button>
        <div className="flex items-center gap-3 text-[11px] uppercase tracking-wider text-slate-400">
          <span className="h-px flex-1 bg-slate-200" /> oppure <span className="h-px flex-1 bg-slate-200" />
        </div>
        <Button type="button" data-testid="login-google-btn" variant="outline" onClick={googleLogin} className="h-11 w-full gap-2 rounded-xl bg-white">
          <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
            <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.4h6.5a5.6 5.6 0 0 1-2.4 3.6v3h3.9c2.3-2.1 3.5-5.2 3.5-8.7z" />
            <path fill="#34A853" d="M12 24c3.2 0 6-1.1 8-2.9l-3.9-3c-1.1.7-2.5 1.2-4.1 1.2-3.1 0-5.8-2.1-6.7-5H1.3v3.1A12 12 0 0 0 12 24z" />
            <path fill="#FBBC05" d="M5.3 14.3a7.2 7.2 0 0 1 0-4.6V6.6h-4a12 12 0 0 0 0 10.8l4-3.1z" />
            <path fill="#EA4335" d="M12 4.8c1.8 0 3.3.6 4.6 1.8l3.4-3.4A12 12 0 0 0 1.3 6.6l4 3.1c.9-2.8 3.6-4.9 6.7-4.9z" />
          </svg>
          Accedi con Google
        </Button>
        <p className="text-center text-xs text-slate-500">Non hai un account? Chiedilo all'amministratore.</p>
      </form>
    </AuthCard>
  );
}
